<template>
  <div class="fixed inset-0 z-[110]">
    <div
      class="absolute inset-0 bg-black/35"
      @click="$emit('close')"
    />

    <div class="relative h-full flex items-center justify-center p-4">
      <div class="w-full max-w-2xl bg-base-100 rounded-xl shadow-xl border border-base-300 p-4">
        <div class="flex items-center justify-between mb-3">
          <div>
            <h3 class="font-semibold text-lg">{{ t('manage_access') }}</h3>
            <p class="text-xs opacity-70">{{ resource.resourceType }} #{{ resource.resourceId }}</p>
          </div>
          <button
            class="btn btn-sm btn-ghost"
            @click="$emit('close')"
          >{{ t('close') }}</button>
        </div>

        <div class="border border-base-300 rounded-lg p-3 mb-3">
          <p class="text-xs opacity-70 mb-2">{{ t('add_permission') }}</p>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-2">
            <UserSearchInput
              ref="userSearch"
              :resource="resource"
              :i18n="i18n"
              @select="selectedUser = $event"
            />
            <select
              v-model="newAction"
              class="select select-bordered select-sm"
            >
              <option
                v-for="action in actions"
                :key="action"
                :value="action"
              >{{ actionLabel(action) }}</option>
            </select>
            <button
              class="btn btn-sm btn-neutral"
              :disabled="!selectedUser || creating"
              @click.prevent="addPermission"
            >
              <span
                v-if="creating"
                class="loading loading-spinner loading-xs"
              />
              <span v-else>{{ t('add') }}</span>
            </button>
          </div>
        </div>

        <div class="border border-base-300 rounded-lg p-3 mb-3">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-sm font-medium">{{ t('general_access') }}</p>
              <p class="text-xs opacity-70 mt-0.5">{{ generalAccessHint }}</p>
            </div>
            <input
              type="checkbox"
              class="toggle toggle-primary"
              :checked="!!generalPermission"
              :disabled="updatingGeneral"
              @change="toggleGeneralAccess($event.target.checked)"
            >
          </div>

          <div
            v-if="generalPermission"
            class="mt-3 flex items-center justify-between gap-3"
          >
            <div class="text-sm opacity-80">{{ t('role_for_everyone') }}</div>
            <select
              class="select select-bordered select-sm w-36"
              :value="generalPermission.action"
              :disabled="updatingGeneral"
              @change="updateGeneralAccessAction($event.target.value)"
            >
              <option value="view">{{ t('permission_role_viewer') }}</option>
              <option value="edit">{{ t('permission_role_editor') }}</option>
            </select>
          </div>
        </div>

        <div class="min-h-24">
          <div
            v-if="loading"
            class="py-8 text-center"
          >
            <span class="loading loading-spinner loading-md" />
          </div>
          <template v-else>
            <div
              v-if="!userRows.length"
              class="text-sm opacity-70 py-5"
            >
              {{ t('no_explicit_permissions') }}
            </div>
            <PermissionRow
              v-for="row in userRows"
              :key="row.id"
              :row="row"
              :actions="actions"
              @change="changePermission"
              @remove="removePermission"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PermissionRow from './PermissionRow'
import UserSearchInput from './UserSearchInput'
import { permissionsStore } from '../lib/permissions_store'
import { showToast } from '../lib/toast'

export default {
  name: 'PermissionModal',
  components: {
    PermissionRow,
    UserSearchInput
  },
  props: {
    resource: {
      type: Object,
      required: true
    },
    i18n: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['close'],
  data () {
    return {
      actions: ['view', 'edit', 'send', 'sign', 'admin'],
      selectedUser: null,
      newAction: 'view',
      creating: false,
      updatingGeneral: false
    }
  },
  computed: {
    storeState () {
      return permissionsStore.state
    },
    resourceKey () {
      return `${this.resource.resourceType}:${this.resource.resourceId}`
    },
    rows () {
      return this.storeState.permissionsByResource[this.resourceKey] || []
    },
    userRows () {
      return this.rows.filter((row) => row.subject_type !== 'Account')
    },
    generalPermission () {
      return this.rows.find((row) => row.subject_type === 'Account')
    },
    loading () {
      return !!this.storeState.loadingByResource[this.resourceKey]
    },
    resourceTypeLabel () {
      const key = `resource_type_${this.resource.resourceType.toLowerCase()}`
      return this.t(key)
    },
    isNestedFolder () {
      return this.resource.resourceType === 'TemplateFolder' && !!this.resource.parentFolderId
    },
    generalAccessHint () {
      const key = this.isNestedFolder ? 'general_access_hint_nested' : 'general_access_hint'
      return this.t(key, { resource_type: this.resourceTypeLabel })
    },
    generalAccessSubjectName () {
      return this.isNestedFolder
        ? this.t('everyone_with_parent_access')
        : this.t('everyone_in_workspace')
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    t (key, vars = {}) {
      let text = this.i18n[key] || key

      Object.entries(vars).forEach(([name, value]) => {
        text = text.replace(new RegExp(`%\\{${name}\\}`, 'g'), value)
      })

      return text
    },
    actionLabel (action) {
      return this.t(`permission_action_${action}`)
    },
    async load () {
      try {
        await permissionsStore.fetchPermissions(this.resource, { force: true })
      } catch (error) {
        showToast(error.message || this.t('failed_to_load_permissions'), 'error')
      }
    },
    async reloadPermissions () {
      await permissionsStore.fetchPermissions(this.resource, { force: true })
    },
    async addPermission () {
      if (!this.selectedUser) return

      this.creating = true
      const user = this.selectedUser

      try {
        await permissionsStore.createPermission(this.resource, {
          permission_action: this.newAction,
          subject_type: 'User',
          subject_id: user.id,
          resource_type: this.resource.resourceType,
          resource_id: this.resource.resourceId,
          subject: {
            id: user.id,
            name: user.name,
            email: user.email
          }
        })

        this.selectedUser = null
        this.$refs.userSearch?.clear?.()
        showToast(this.t('permission_added'), 'success')
      } catch (error) {
        showToast(error.message || this.t('failed_to_add_permission'), 'error')
      } finally {
        this.creating = false
      }
    },
    async changePermission (row, action) {
      if (row.action === action) return

      try {
        await permissionsStore.updatePermission(this.resource, row.id, action)
        showToast(this.t('permission_updated'), 'success')
      } catch (error) {
        showToast(error.message || this.t('failed_to_update_permission'), 'error')
      }
    },
    async removePermission (row) {
      const email = row.subject?.email || this.t('remove_access_confirm_fallback')
      if (!window.confirm(this.t('remove_access_confirm', { email }))) {
        return
      }

      try {
        await permissionsStore.removePermission(this.resource, row.id)
        showToast(this.t('permission_removed'), 'success')
      } catch (error) {
        showToast(error.message || this.t('failed_to_remove_permission'), 'error')
      }
    },
    async toggleGeneralAccess (enabled) {
      if (enabled) {
        return this.createGeneralAccess()
      }

      if (!this.generalPermission) return

      this.updatingGeneral = true

      try {
        await permissionsStore.removePermission(this.resource, this.generalPermission.id)
        await this.reloadPermissions()
        showToast(this.t('general_access_disabled'), 'success')
      } catch (error) {
        showToast(error.message || this.t('failed_to_update_general_access'), 'error')
      } finally {
        this.updatingGeneral = false
      }
    },
    async createGeneralAccess () {
      this.updatingGeneral = true

      try {
        await permissionsStore.createPermission(this.resource, {
          permission_action: 'view',
          subject_type: 'Account',
          subject_id: 'self',
          resource_type: this.resource.resourceType,
          resource_id: this.resource.resourceId,
          subject: {
            id: 'self',
            name: this.generalAccessSubjectName,
            email: null
          }
        })
        await this.reloadPermissions()
        showToast(this.t('general_access_enabled'), 'success')
      } catch (error) {
        showToast(error.message || this.t('failed_to_enable_general_access'), 'error')
      } finally {
        this.updatingGeneral = false
      }
    },
    async updateGeneralAccessAction (action) {
      if (!this.generalPermission) return
      if (!['view', 'edit'].includes(action)) return
      if (this.generalPermission.action === action) return

      this.updatingGeneral = true

      try {
        await permissionsStore.updatePermission(this.resource, this.generalPermission.id, action)
        await this.reloadPermissions()
        showToast(this.t('general_access_updated'), 'success')
      } catch (error) {
        showToast(error.message || this.t('failed_to_update_general_access'), 'error')
      } finally {
        this.updatingGeneral = false
      }
    }
  }
}
</script>
